import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { db } from "../../lib/firebase";
import { collection, query, where, getDocs, doc, getDoc, deleteDoc } from "firebase/firestore";
import { useAuthContext } from "../../contexts/AuthContext";
import { Users, BookOpen, Clock, TrendingUp, Search, Eye, DollarSign, Trash2, AlertTriangle, ArrowLeft } from "lucide-react";

interface StudentProgress {
  userId: string;
  userName: string;
  userEmail: string;
  userPhoto?: string;
  totalCourses: number;
  completedLessons: number;
  totalLessons: number;
  averageProgress: number;
  lastLesson: string;
  lastAccessDate: Date | null;
  totalRevenue: number;
}

export default function StudentReports() {
  const { user } = useAuthContext();
  const [students, setStudents] = useState<StudentProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean, studentId: string, studentName: string}>({show: false, studentId: "", studentName: ""});
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function loadStudentData() {
      if (!user) return;

      try {
        // Get all courses by this instructor
        const coursesQuery = query(
          collection(db, "courses"),
          where("instructorId", "==", user.uid)
        );
        const coursesSnap = await getDocs(coursesQuery);
        const courseIds = coursesSnap.docs.map(doc => doc.id);
        const coursesData = coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        if (courseIds.length === 0) {
          setLoading(false);
          return;
        }

        // Get all payments for instructor's courses - only approved payments
        const paymentsQuery = query(
          collection(db, "payments"),
          where("courseId", "in", courseIds.slice(0, 10)),
          where("status", "==", "completed")
        );
        const paymentsSnap = await getDocs(paymentsQuery);
        const paymentsData = paymentsSnap.docs
          .filter(doc => doc.data().approvedBy)
          .map(doc => doc.data());

        // Get all progress records for instructor's courses
        const progressQuery = query(
          collection(db, "progress"),
          where("courseId", "in", courseIds.slice(0, 10)) // Firestore limit
        );
        const progressSnap = await getDocs(progressQuery);

        // Group progress by student
        const studentMap = new Map<string, any>();

        for (const progressDoc of progressSnap.docs) {
          const progressData = progressDoc.data();
          const userId = progressData.userId;

          if (!studentMap.has(userId)) {
            // Load user data
            const userDoc = await getDoc(doc(db, "users", userId));
            const userData = userDoc.exists() ? userDoc.data() : {};

            studentMap.set(userId, {
              userId,
              userName: userData.name || "ไม่ระบุชื่อ",
              userEmail: userData.email || "",
              userPhoto: userData.profileImage || "",
              courses: new Set<string>(),
              completedLessons: 0,
              totalLessons: 0,
              lastAccessDate: null,
              lastLesson: "",
              totalRevenue: 0
            });
          }

          const student = studentMap.get(userId);
          student.courses.add(progressData.courseId);
          
          if (progressData.completed) {
            student.completedLessons++;
          }

          // Track last access
          if (progressData.completedAt) {
            const accessDate = progressData.completedAt.toDate();
            if (!student.lastAccessDate || accessDate > student.lastAccessDate) {
              student.lastAccessDate = accessDate;
              
              // Get lesson title
              const lessonDoc = await getDoc(doc(db, "lessons", progressData.lessonId));
              if (lessonDoc.exists()) {
                student.lastLesson = lessonDoc.data().title || "ไม่ระบุ";
              }
            }
          }
        }

        // Calculate total lessons for each student's courses
        const studentList: StudentProgress[] = [];
        
        for (const [userId, studentData] of studentMap.entries()) {
          let totalLessons = 0;
          
          for (const courseId of studentData.courses) {
            const lessonsQuery = query(
              collection(db, "lessons"),
              where("courseId", "==", courseId)
            );
            const lessonsSnap = await getDocs(lessonsQuery);
            totalLessons += lessonsSnap.size;
          }

          const averageProgress = totalLessons > 0 
            ? Math.round((studentData.completedLessons / totalLessons) * 100) 
            : 0;

          // Calculate revenue from this student (actual payment amounts)
          let studentRevenue = 0;
          for (const payment of paymentsData) {
            if (payment.userId === userId && studentData.courses.has(payment.courseId)) {
              studentRevenue += Number(payment.amount || 0);
            }
          }

          studentList.push({
            userId: studentData.userId,
            userName: studentData.userName,
            userEmail: studentData.userEmail,
            userPhoto: studentData.userPhoto,
            totalCourses: studentData.courses.size,
            completedLessons: studentData.completedLessons,
            totalLessons,
            averageProgress,
            lastLesson: studentData.lastLesson,
            lastAccessDate: studentData.lastAccessDate,
            totalRevenue: studentRevenue
          });
        }

        // Sort by last access date (most recent first)
        studentList.sort((a, b) => {
          if (!a.lastAccessDate) return 1;
          if (!b.lastAccessDate) return -1;
          return b.lastAccessDate.getTime() - a.lastAccessDate.getTime();
        });

        setStudents(studentList);
        setLoading(false);
      } catch (error) {
        console.error("Error loading student data:", error);
        setLoading(false);
      }
    }

    loadStudentData();
  }, [user]);

  const filteredStudents = students.filter(student =>
    student.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.userEmail.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (date: Date | null) => {
    if (!date) return "ไม่มีข้อมูล";
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!user) return;
    
    setDeleting(true);
    try {
      // Get all courses by this instructor
      const coursesQuery = query(
        collection(db, "courses"),
        where("instructorId", "==", user.uid)
      );
      const coursesSnap = await getDocs(coursesQuery);
      const courseIds = coursesSnap.docs.map(doc => doc.id);

      // Delete all progress records for this student in instructor's courses
      const progressQuery = query(
        collection(db, "progress"),
        where("userId", "==", studentId),
        where("courseId", "in", courseIds.slice(0, 10))
      );
      const progressSnap = await getDocs(progressQuery);
      
      const deletePromises = progressSnap.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);

      // Remove student from local state
      setStudents(prev => prev.filter(s => s.userId !== studentId));
      setDeleteConfirm({show: false, studentId: "", studentName: ""});
    } catch (error) {
      console.error("Error deleting student data:", error);
      alert("เกิดข้อผิดพลาดในการลบข้อมูล");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">กำลังโหลดข้อมูลนักเรียน...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Link to="/instructor/dashboard" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6 font-semibold transition-colors">
          <ArrowLeft className="w-5 h-5" />
          กลับไปหน้า Dashboard
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-pink-600 rounded-xl flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">รายงานนักเรียน</h1>
              <p className="text-gray-600">ติดตามความคืบหน้าของนักเรียนทั้งหมด</p>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">นักเรียนทั้งหมด</p>
                <p className="text-3xl font-bold text-gray-800">{students.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">ความคืบหน้าเฉลี่ย</p>
                <p className="text-3xl font-bold text-gray-800">
                  {students.length > 0
                    ? Math.round(students.reduce((sum, s) => sum + s.averageProgress, 0) / students.length)
                    : 0}%
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <BookOpen className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <p className="text-gray-600 text-sm">บทเรียนที่เสร็จแล้ว</p>
                <p className="text-3xl font-bold text-gray-800">
                  {students.reduce((sum, s) => sum + s.completedLessons, 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="ค้นหาชื่อหรืออีเมลนักเรียน..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-gray-800 shadow-sm"
            />
          </div>
        </div>

        {/* Student List */}
        {filteredStudents.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">ยังไม่มีนักเรียน</h3>
            <p className="text-gray-600">
              {searchTerm ? "ไม่พบนักเรียนที่ค้นหา" : "เมื่อมีคนซื้อคอร์สของคุณ จะแสดงที่นี่"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredStudents.map((student) => (
              <div
                key={student.userId}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                  {/* Student Info */}
                  <div className="flex items-center gap-4 flex-1">
                    {student.userPhoto ? (
                      <img
                        src={student.userPhoto}
                        alt={student.userName}
                        className="w-16 h-16 rounded-full object-cover ring-4 ring-purple-100"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-pink-600 flex items-center justify-center text-white text-2xl font-bold">
                        {student.userName[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-800 truncate">{student.userName}</h3>
                      <p className="text-sm text-gray-600 truncate">{student.userEmail}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <BookOpen className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-600">{student.totalCourses} คอร์ส</span>
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">ความคืบหน้า</span>
                      <span className="text-lg font-bold text-blue-600">{student.averageProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-pink-600 h-3 rounded-full transition-all duration-500"
                        style={{ width: `${student.averageProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      {student.completedLessons}/{student.totalLessons} บทเรียน
                    </p>
                  </div>

                  {/* Revenue */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-600">ยอดชำระ</span>
                    </div>
                    <p className="text-lg font-bold text-green-600">
                      ฿{student.totalRevenue.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {student.totalCourses} คอร์ส
                    </p>
                  </div>

                  {/* Last Activity */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">เข้าเรียนล่าสุด</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800">{formatDate(student.lastAccessDate)}</p>
                    {student.lastLesson && (
                      <p className="text-xs text-gray-600 mt-1 truncate">บท: {student.lastLesson}</p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Link to={`/instructor/student/${student.userId}`}>
                      <button className="bg-gradient-to-r from-blue-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2 whitespace-nowrap">
                        <Eye className="w-5 h-5" />
                        ดูรายละเอียด
                      </button>
                    </Link>
                    <button 
                      onClick={() => setDeleteConfirm({show: true, studentId: student.userId, studentName: student.userName})}
                      className="bg-red-500 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-red-600 transition-all duration-300 transform hover:scale-105 flex items-center gap-2 whitespace-nowrap"
                    >
                      <Trash2 className="w-5 h-5" />
                      ลบข้อมูล
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">ยืนยันการลบข้อมูล</h3>
                  <p className="text-gray-600 mt-1">การดำเนินการนี้ไม่สามารถย้อนกลับได้</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <p className="text-gray-800">
                  คุณต้องการลบข้อมูลความคืบหน้าทั้งหมดของ
                </p>
                <p className="font-bold text-gray-900 mt-2 text-lg">
                  {deleteConfirm.studentName}
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  ข้อมูลการเรียนทั้งหมดในคอร์สของคุณจะถูกลบถาวร
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm({show: false, studentId: "", studentName: ""})}
                  disabled={deleting}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all duration-300 disabled:opacity-50"
                >
                  ยกเลิก
                </button>
                <button
                  onClick={() => handleDeleteStudent(deleteConfirm.studentId)}
                  disabled={deleting}
                  className="flex-1 bg-red-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-red-700 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังลบ...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-5 h-5" />
                      ลบข้อมูล
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
